import { CRM_CONTACT_EMAIL_METADATA_KEY } from 'app/pages/crm/shared/crm.constants';
import {
  contactCustomFieldDefinitionsApi,
  contactTaskApi,
  crmContactApi,
  crmTagApi,
  emailNotificationSkynetApi,
  emailNotificationsNotificationsApi,
} from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

import {
  enrichLinkedApplications,
  pageContactsClientSide,
  warnOnceOnUnpagedContactsResponse,
} from './crm.actions.helpers';

export const CRM_ACTION_TYPE = createNamedActions('crm', {
  GET_CRM_CONTACTS: 'GET_CRM_CONTACTS',
  GET_CRM_CONTACT_BY_ID: 'GET_CRM_CONTACT_BY_ID',
  GET_CRM_CONTACTS_BY_APPLICATION: 'GET_CRM_CONTACTS_BY_APPLICATION',
  GET_CRM_CONTACTS_BY_LEAD: 'GET_CRM_CONTACTS_BY_LEAD',
  CREATE_CRM_CONTACT: 'CREATE_CRM_CONTACT',
  GET_CRM_TAGS: 'GET_CRM_TAGS',
  CREATE_CRM_TAG: 'CREATE_CRM_TAG',
  UPDATE_CRM_TAG: 'UPDATE_CRM_TAG',
  DELETE_CRM_TAG: 'DELETE_CRM_TAG',
  GET_CONTACT_NOTES: 'GET_CONTACT_NOTES',
  CREATE_CONTACT_NOTE: 'CREATE_CONTACT_NOTE',
  UPDATE_CONTACT_NOTE: 'UPDATE_CONTACT_NOTE',
  DELETE_CONTACT_NOTE: 'DELETE_CONTACT_NOTE',
  GET_CONTACT_ACTIVITY_LOG: 'GET_CONTACT_ACTIVITY_LOG',
  GET_CONTACT_EMAILS: 'GET_CONTACT_EMAILS',
  CONTACT_EMAIL_SENT: 'CONTACT_EMAIL_SENT',
  GET_CONTACT_APPLICATION_LINKS: 'GET_CONTACT_APPLICATION_LINKS',
  LINK_CONTACT_TO_APPLICATION: 'LINK_CONTACT_TO_APPLICATION',
  UNLINK_CONTACT_FROM_APPLICATION: 'UNLINK_CONTACT_FROM_APPLICATION',
  UPDATE_CRM_CONTACT: 'UPDATE_CRM_CONTACT',
  DELETE_CRM_CONTACT: 'DELETE_CRM_CONTACT',
  RESET_CRM_STATE: 'RESET_CRM_STATE',
  GET_CONTACT_TASKS: 'GET_CONTACT_TASKS',
  CREATE_CONTACT_TASK: 'CREATE_CONTACT_TASK',
  UPDATE_CONTACT_TASK: 'UPDATE_CONTACT_TASK',
  COMPLETE_CONTACT_TASK: 'COMPLETE_CONTACT_TASK',
  DELETE_CONTACT_TASK: 'DELETE_CONTACT_TASK',
  GET_CONTACT_CHILDREN: 'GET_CONTACT_CHILDREN',
  GET_CONTACT_SIBLINGS: 'GET_CONTACT_SIBLINGS',
  UPDATE_CONTACT_PARENT: 'UPDATE_CONTACT_PARENT',
  GET_CONTACT_SUB_COMPANY_LINKS: 'GET_CONTACT_SUB_COMPANY_LINKS',
  LINK_CONTACT_TO_SUB_COMPANY: 'LINK_CONTACT_TO_SUB_COMPANY',
  UNLINK_CONTACT_FROM_SUB_COMPANY: 'UNLINK_CONTACT_FROM_SUB_COMPANY',
  SET_CONTACT_CUSTOM_FIELD_DEFINITIONS: 'SET_CONTACT_CUSTOM_FIELD_DEFINITIONS',
  ADD_CONTACT_CUSTOM_FIELD_DEFINITION: 'ADD_CONTACT_CUSTOM_FIELD_DEFINITION',
  UPDATE_CONTACT_CUSTOM_FIELD_DEFINITION: 'UPDATE_CONTACT_CUSTOM_FIELD_DEFINITION',
  DELETE_CONTACT_CUSTOM_FIELD_DEFINITION: 'DELETE_CONTACT_CUSTOM_FIELD_DEFINITION',
  UPSERT_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE:
    'UPSERT_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE',
  DELETE_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE:
    'DELETE_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE',
  SET_CONTACT_CUSTOM_FIELD_VALUES: 'SET_CONTACT_CUSTOM_FIELD_VALUES',
  CREATE_CONTACT_APPLICATION: 'CREATE_CONTACT_APPLICATION',
});

export const getCrmContactsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.getContacts(params);

  // The endpoint answers a bare array when unpaged and a { items, totalCount } envelope when
  // paging params are supplied; normalize both so the list can page against the same action.
  const isUnpagedResponse = Array.isArray(data);
  const contacts = isUnpagedResponse ? data : (data?.items ?? []);
  const totalCount = isUnpagedResponse ? data.length : (data?.totalCount ?? contacts.length);

  // A bare array back from a paged request means the CRM API predates Lender.Optimus.CRM#125:
  // page/pageSize/search/lifecycleStage were ignored and the whole set came back, so the pager
  // and filters would look functional while doing nothing. Apply them here instead.
  const isUnpagedResponseToPagedRequest = isUnpagedResponse && !!params?.page;

  warnOnceOnUnpagedContactsResponse(isUnpagedResponseToPagedRequest);

  return dispatch({
    type: CRM_ACTION_TYPE.GET_CRM_CONTACTS,
    payload: isUnpagedResponseToPagedRequest
      ? pageContactsClientSide(contacts, params)
      : { contacts, totalCount },
  });
});

export const getCrmContactDuplicatesAction = catchAsyncActionError(
  ({ page, pageSize }) =>
    async () => {
      const { data } = await crmContactApi.getContactDuplicates({ page, pageSize });

      return data;
    },
);

export const mergeCrmContactAction = catchAsyncActionError(
  ({ contactId, sourceContactId }) =>
    async () => {
      const { data } = await crmContactApi.mergeContacts({ contactId, sourceContactId });

      return data;
    },
);

export const getCrmContactByIdAction = catchAsyncActionError(contactId => async dispatch => {
  const { data } = await crmContactApi.getContactById(contactId);

  return dispatch({
    type: CRM_ACTION_TYPE.GET_CRM_CONTACT_BY_ID,
    payload: data,
  });
});

export const createCrmContactAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.createContact(params);

  return dispatch({
    type: CRM_ACTION_TYPE.CREATE_CRM_CONTACT,
    payload: data,
  });
});

export const getCrmTagsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmTagApi.getTags(params);

  return dispatch({
    type: CRM_ACTION_TYPE.GET_CRM_TAGS,
    payload: data,
  });
});

export const createCrmTagAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmTagApi.createTag(params);

  return dispatch({
    type: CRM_ACTION_TYPE.CREATE_CRM_TAG,
    payload: data,
  });
});

export const updateCrmTagAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmTagApi.updateTag(params);

  return dispatch({
    type: CRM_ACTION_TYPE.UPDATE_CRM_TAG,
    payload: data,
  });
});

export const deleteCrmTagAction = catchAsyncActionError(tagId => async dispatch => {
  await crmTagApi.deleteTag(tagId);

  return dispatch({
    type: CRM_ACTION_TYPE.DELETE_CRM_TAG,
    payload: tagId,
  });
});

export const getContactNotesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.getContactNotes(params);

  return dispatch({
    type: CRM_ACTION_TYPE.GET_CONTACT_NOTES,
    payload: data,
  });
});

export const createContactNoteAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.createContactNote(params);

  return dispatch({
    type: CRM_ACTION_TYPE.CREATE_CONTACT_NOTE,
    payload: data,
  });
});

export const updateContactNoteAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.updateContactNote(params);

  return dispatch({
    type: CRM_ACTION_TYPE.UPDATE_CONTACT_NOTE,
    payload: data,
  });
});

export const deleteContactNoteAction = catchAsyncActionError(params => async dispatch => {
  await crmContactApi.deleteContactNote(params);

  return dispatch({
    type: CRM_ACTION_TYPE.DELETE_CONTACT_NOTE,
    payload: params.noteId,
  });
});

export const getContactActivityLogAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.getContactActivityLog(params);

  return dispatch({
    type: CRM_ACTION_TYPE.GET_CONTACT_ACTIVITY_LOG,
    payload: data,
  });
});

export const getContactApplicationLinksAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.getContactApplicationLinks(params);
  const applicationLinks = await enrichLinkedApplications(data?.applicationLinks ?? []);

  return dispatch({
    type: CRM_ACTION_TYPE.GET_CONTACT_APPLICATION_LINKS,
    payload: applicationLinks,
  });
});

export const linkContactToApplicationAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await crmContactApi.linkContactToApplication(params);

  return dispatch({
    type: CRM_ACTION_TYPE.LINK_CONTACT_TO_APPLICATION,
    payload: data,
  });
});

export const unlinkContactFromApplicationAction = catchAsyncActionError(
  ({ contactId, applicationId }) =>
    async dispatch => {
      await crmContactApi.unlinkContactFromApplication({ contactId, applicationId });

      return dispatch({
        type: CRM_ACTION_TYPE.UNLINK_CONTACT_FROM_APPLICATION,
        payload: applicationId,
      });
    },
);

export const getContactEmailsAction = catchAsyncActionError(
  ({ contactId, signal }) =>
    async dispatch => {
      const { data } = await emailNotificationsNotificationsApi.getEmails({
        metadata: `${CRM_CONTACT_EMAIL_METADATA_KEY}:${contactId}`,
        signal,
      });

      return dispatch({
        type: CRM_ACTION_TYPE.GET_CONTACT_EMAILS,
        payload: data ?? [],
      });
    },
);

export const sendContactEmailAction = catchAsyncActionError(
  ({ contactId, payload }) =>
    async dispatch => {
      await emailNotificationSkynetApi.sendContactEmail({ contactId, payload });

      return dispatch({
        type: CRM_ACTION_TYPE.CONTACT_EMAIL_SENT,
        payload: { contactId },
      });
    },
);

export const deleteCrmContactAction = catchAsyncActionError(contactId => async dispatch => {
  await crmContactApi.deleteContact(contactId);

  return dispatch({
    type: CRM_ACTION_TYPE.DELETE_CRM_CONTACT,
    payload: contactId,
  });
});

export const logContactActivityAction = catchAsyncActionError(
  ({ contactId, ...body }) =>
    async dispatch => {
      const { data } = await crmContactApi.logContactActivity({ contactId, ...body });

      return dispatch({
        type: CRM_ACTION_TYPE.GET_CONTACT_ACTIVITY_LOG,
        payload: data,
      });
    },
);

export const getContactTasksAction = catchAsyncActionError(({ contactId }) => async dispatch => {
  const { data } = await contactTaskApi.getContactTasks({ contactId });

  return dispatch({
    type: CRM_ACTION_TYPE.GET_CONTACT_TASKS,
    payload: { contactId, tasks: data ?? [] },
  });
});

export const createContactTaskAction = catchAsyncActionError(
  ({ contactId, taskTypeId, name, description, availableForAssignees, assignedUserId }) =>
    async dispatch => {
      const { data } = await contactTaskApi.createContactTask({
        contactId,
        taskTypeId,
        name,
        description,
        availableForAssignees: availableForAssignees ?? false,
        assignedUserId: assignedUserId ?? null,
      });

      return dispatch({
        type: CRM_ACTION_TYPE.CREATE_CONTACT_TASK,
        payload: { contactId, task: data },
      });
    },
);

export const completeContactTaskAction = catchAsyncActionError(
  ({ contactId, taskId }) =>
    async dispatch => {
      const { data } = await contactTaskApi.completeContactTask({ taskId });

      return dispatch({
        type: CRM_ACTION_TYPE.COMPLETE_CONTACT_TASK,
        payload: { contactId, task: data, taskId },
      });
    },
);

export const deleteContactTaskAction = catchAsyncActionError(
  ({ contactId, taskId }) =>
    async dispatch => {
      await contactTaskApi.deleteContactTask({ taskId });

      return dispatch({
        type: CRM_ACTION_TYPE.DELETE_CONTACT_TASK,
        payload: { contactId, taskId },
      });
    },
);

export const getContactChildrenAction = catchAsyncActionError(
  ({ contactId, page, pageSize }) =>
    async dispatch => {
      const { data } = await crmContactApi.getContactChildren({ contactId, page, pageSize });

      return dispatch({
        type: CRM_ACTION_TYPE.GET_CONTACT_CHILDREN,
        payload: { contactId, items: data?.items ?? [], totalCount: data?.totalCount ?? 0 },
      });
    },
);

export const getContactSiblingsAction = catchAsyncActionError(
  ({ parentContactId }) =>
    async dispatch => {
      const { data } = await crmContactApi.getContactChildren({ contactId: parentContactId });

      return dispatch({
        type: CRM_ACTION_TYPE.GET_CONTACT_SIBLINGS,
        payload: data?.items ?? [],
      });
    },
);

export const updateContactParentAction = catchAsyncActionError(
  ({ contactId, parentContactId }) =>
    async dispatch => {
      await crmContactApi.updateContactParent({ contactId, parentContactId });

      return dispatch({
        type: CRM_ACTION_TYPE.UPDATE_CONTACT_PARENT,
        payload: { contactId, parentContactId },
      });
    },
);

export const getContactSubCompanyLinksAction = catchAsyncActionError(
  ({ contactId }) =>
    async dispatch => {
      const { data } = await crmContactApi.getContactSubCompanyLinks({ contactId });

      return dispatch({
        type: CRM_ACTION_TYPE.GET_CONTACT_SUB_COMPANY_LINKS,
        payload: Array.isArray(data) ? data : [],
      });
    },
);

export const linkContactToSubCompanyAction = catchAsyncActionError(
  ({ contactId, subCompanyId }) =>
    async dispatch => {
      const { data } = await crmContactApi.linkContactToSubCompany({ contactId, subCompanyId });

      return dispatch({
        type: CRM_ACTION_TYPE.LINK_CONTACT_TO_SUB_COMPANY,
        payload: data,
      });
    },
);

export const unlinkContactFromSubCompanyAction = catchAsyncActionError(
  ({ contactId, subCompanyId }) =>
    async dispatch => {
      await crmContactApi.unlinkContactFromSubCompany({ contactId, subCompanyId });

      return dispatch({
        type: CRM_ACTION_TYPE.UNLINK_CONTACT_FROM_SUB_COMPANY,
        payload: subCompanyId,
      });
    },
);

export const loadContactCustomFieldDefinitionsAction = catchAsyncActionError(
  (params = {}) =>
    async dispatch => {
      const response =
        await contactCustomFieldDefinitionsApi.getContactCustomFieldDefinitions(params);
      const definitions = Array.isArray(response.data) ? response.data : [];

      dispatch({
        type: CRM_ACTION_TYPE.SET_CONTACT_CUSTOM_FIELD_DEFINITIONS,
        payload: definitions,
      });

      return definitions;
    },
  'Failed to load contact custom field definitions',
);

export const createContactCustomFieldDefinitionAction = catchAsyncActionError(
  data => async dispatch => {
    const response =
      await contactCustomFieldDefinitionsApi.createContactCustomFieldDefinition(data);
    const created = response.data;

    if (created?.id) {
      dispatch({
        type: CRM_ACTION_TYPE.ADD_CONTACT_CUSTOM_FIELD_DEFINITION,
        payload: created,
      });
    }

    dispatch(
      showMessageAction({
        message: 'Contact custom field created successfully',
        variant: 'success',
      }),
    );

    return created;
  },
  'Failed to create contact custom field definition',
);

export const updateContactCustomFieldDefinitionAction = catchAsyncActionError(
  payload => async dispatch => {
    const { id, ...data } = payload;

    if (!id) {
      throw new Error('Missing ID for update');
    }

    const response = await contactCustomFieldDefinitionsApi.updateContactCustomFieldDefinition(
      id,
      data,
    );
    const updated = { ...payload, ...response.data, id };

    dispatch({
      type: CRM_ACTION_TYPE.UPDATE_CONTACT_CUSTOM_FIELD_DEFINITION,
      payload: updated,
    });

    dispatch(
      showMessageAction({
        message: 'Contact custom field updated successfully',
        variant: 'success',
      }),
    );

    return updated;
  },
  'Failed to update contact custom field definition',
);

export const deleteContactCustomFieldDefinitionAction = catchAsyncActionError(
  ({ id }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for delete');
      }

      await contactCustomFieldDefinitionsApi.deleteContactCustomFieldDefinition(id);

      dispatch({
        type: CRM_ACTION_TYPE.DELETE_CONTACT_CUSTOM_FIELD_DEFINITION,
        payload: id,
      });

      dispatch(
        showMessageAction({
          message: 'Contact custom field deleted successfully',
          variant: 'success',
        }),
      );

      return id;
    },
  'Failed to delete contact custom field definition',
);

export const upsertContactCustomFieldDefinitionOverrideAction = catchAsyncActionError(
  ({ id, ...data }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for override upsert');
      }

      const response =
        await contactCustomFieldDefinitionsApi.upsertContactCustomFieldDefinitionOverride(id, data);

      dispatch({
        type: CRM_ACTION_TYPE.UPSERT_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE,
        payload: { id, override: response.data ?? null },
      });

      dispatch(
        showMessageAction({
          message: 'Contact custom field updated successfully',
          variant: 'success',
        }),
      );

      return response.data;
    },
  'Failed to update contact custom field override',
);

export const deleteContactCustomFieldDefinitionOverrideAction = catchAsyncActionError(
  ({ id }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for override delete');
      }

      await contactCustomFieldDefinitionsApi.deleteContactCustomFieldDefinitionOverride(id);

      dispatch({
        type: CRM_ACTION_TYPE.DELETE_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE,
        payload: { id },
      });

      dispatch(
        showMessageAction({
          message: 'Contact custom field updated successfully',
          variant: 'success',
        }),
      );

      return id;
    },
  'Failed to delete contact custom field override',
);

export const loadContactCustomFieldValuesAction = catchAsyncActionError(
  contactId => async dispatch => {
    const response = await contactCustomFieldDefinitionsApi.getContactCustomFieldValues(contactId);
    const values = Array.isArray(response.data) ? response.data : [];

    dispatch({
      type: CRM_ACTION_TYPE.SET_CONTACT_CUSTOM_FIELD_VALUES,
      payload: values,
    });

    return values;
  },
  'Failed to load contact custom field values',
);

export const saveContactCustomFieldValuesAction = catchAsyncActionError(
  ({ contactId, values }) =>
    async dispatch => {
      await contactCustomFieldDefinitionsApi.saveContactCustomFieldValues(contactId, values);

      dispatch(loadContactCustomFieldValuesAction(contactId));

      dispatch(showMessageAction({ message: 'Contact custom fields saved', variant: 'success' }));
    },
  'Failed to save contact custom field values',
);

export const createContactApplicationAction = catchAsyncActionError(
  ({ contactId, isTest = false }) =>
    async dispatch => {
      const { data } = await crmContactApi.createApplicationForContact({ contactId, isTest });

      dispatch({
        type: CRM_ACTION_TYPE.CREATE_CONTACT_APPLICATION,
        payload: data,
      });

      return data;
    },
);
